import React, { useEffect, useMemo, useState } from 'react';
import { personalInfo, experience, projects } from '../../data/portfolioData';
import ThemeToggle from './ThemeToggle';
import './Sidebar.css';

const TREE = [
  { id: 'top', label: 'index' },
  {
    id: 'experience',
    label: 'experience/',
    children: experience.map((job) => ({ id: `experience-${job.slug}`, label: job.slug }))
  },
  {
    id: 'projects',
    label: 'projects/',
    children: projects.map((p) => ({ id: `project-${p.slug}`, label: p.slug }))
  },
  { id: 'stack', label: 'stack' },
  { id: 'credentials', label: 'credentials' },
  { id: 'resume', label: 'resume.pdf' },
  { id: 'contact', label: 'contact' }
];

const flatten = (nodes) =>
  nodes.flatMap((n) => [n.id, ...(n.children ? n.children.map((c) => c.id) : [])]);

/**
 * Workspace sidebar: identity, availability, a file tree of the page,
 * and the theme switch. On narrow screens it folds into a top bar.
 */
export const Sidebar = ({ onNavigate }) => {
  const ids = useMemo(() => flatten(TREE), []);
  const [active, setActive] = useState('top');
  const [open, setOpen] = useState(false);

  // The active entry is the last one whose top has scrolled past the
  // reading line. A dozen rect reads per scroll event is cheap, and
  // running it directly avoids stalling when frames are throttled.
  useEffect(() => {
    const update = () => {
      let current = ids[0];
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= 140) current = id;
      });
      setActive(current);
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [ids]);

  const go = (event, id) => {
    event.preventDefault();
    setOpen(false);
    onNavigate(id);
  };

  const link = (node, depth) => {
    const isActive = active === node.id;
    const holdsActive = node.children?.some((c) => c.id === active);
    const cls = ['tree-link', isActive && 'is-active', holdsActive && 'is-open']
      .filter(Boolean)
      .join(' ');
    return (
      <a
        href={`#${node.id}`}
        className={cls}
        style={{ '--depth': depth }}
        aria-current={isActive ? 'location' : undefined}
        onClick={(e) => go(e, node.id)}
      >
        {node.label}
      </a>
    );
  };

  return (
    <aside className={open ? 'sidebar is-open' : 'sidebar'}>
      <div className="sidebar-top">
        <a className="sidebar-name" href="/" onClick={(e) => go(e, 'top')}>
          {personalInfo.name}
          <span className="sidebar-role">{personalInfo.title}</span>
        </a>
        <button
          type="button"
          className="sidebar-toggle"
          aria-expanded={open}
          aria-controls="sidebar-tree"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? 'Close' : 'Files'}
        </button>
      </div>

      <div className="sidebar-body" id="sidebar-tree">
        <p className="sidebar-status">
          <span className="status-lamp" aria-hidden="true" />
          Open to work, Jan 2027
        </p>

        <nav className="tree" aria-label="Page contents">
          <p className="tree-root">chirag-patil/</p>
          <ul>
            {TREE.map((node) => (
              <li key={node.id}>
                {link(node, 1)}
                {node.children && (
                  <ul className="tree-branch">
                    {node.children.map((child) => (
                      <li key={child.id}>{link(child, 2)}</li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <div className="sidebar-foot">
          <div className="sidebar-links">
            <a href={personalInfo.github} target="_blank" rel="noreferrer">
              GitHub
            </a>
            <a href={personalInfo.linkedin} target="_blank" rel="noreferrer">
              LinkedIn
            </a>
            <a href={`mailto:${personalInfo.email}`}>Email</a>
          </div>
          <ThemeToggle />
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
